import * as React from 'react';

import type { ISanityContactForm } from '../fragments/sanity-contact-form-parts';
import type { ISanityCopyWithImage } from '../fragments/sanity-copy-with-image-parts';
import type { ISanityFaqs } from '../fragments/sanity-frequently-asked-questions-parts';
import type { ISanityGoogleMap } from '../fragments/sanity-google-map-parts';
import { ISanityHoursOfOperation } from '../fragments/sanity-hours-of-operation-parts';
import type { ISanityHeadingWithCta } from '../fragments/sanity-heading-with-cta-parts';
import type { ISanityHero } from '../fragments/sanity-hero-parts';
import type { ISanityServicesGrid } from '../fragments/sanity-services-grid-parts';
import { ISanityReviews } from '../fragments/sanity-reviews-parts';
import { ISanityFees } from '../fragments/sanity-fees-parts';
import { ISanityContactInformation } from '../fragments/sanity-contact-information-parts';
import { ISanityHeading } from '../fragments/sanity-heading-parts';
import { ISanityBookeoBookingWidget } from '../fragments/sanity-bookeo-booking-widget-parts';
import { ISanityBookeoVoucherWidget } from '../fragments/sanity-bookeo-voucher-widget-parts';
import { ContactSection } from './modules/contact-section';
import { CopyWithImage } from './modules/copy-with-image';
import { FAQs } from './modules/faqs';
import { GoogleMap } from './modules/google-map';
import { HeadingWithCta } from './modules/heading-with-cta';
import { Hero } from './modules/hero';
import { ServicesGrid } from './modules/services-grid';
import { Reviews } from './modules/reviews';
import { HoursOfOperation } from './modules/hours-of-operation';
import { Fees } from './modules/fees';
import { ContactInformation } from './modules/contact-information';
import { FAQsHome } from './modules/faqs-home';
import { Heading } from './modules/heading';
import { BookeoBookingWidget } from './modules/bookeo-booking-widget';
import { BookeoVoucherWidget } from './modules/bookeo-voucher-widget';

type TSanityModule =
  | ISanityHero
  | ISanityCopyWithImage
  | ISanityServicesGrid
  | ISanityHeadingWithCta
  | ISanityFaqs
  | ISanityContactForm
  | ISanityGoogleMap
  | ISanityHoursOfOperation
  | ISanityReviews
  | ISanityFees
  | ISanityContactInformation
  | ISanityHeading
  | ISanityBookeoBookingWidget
  | ISanityBookeoVoucherWidget;

interface SanityModulesProps {
  modules: TSanityModule[];
  title: string;
}

function SanityModules({ modules, title }: SanityModulesProps): JSX.Element {
  return (
    <>
      {modules.map((module) => {
        if (!module) return null;
        return <SanityModule key={module.id} module={module} title={title} />;
      })}
    </>
  );
}

interface SanityModuleProps {
  module: TSanityModule;
  title: string;
}

function SanityModule({
  module,
  title,
}: SanityModuleProps): JSX.Element | null {
  // console.log(module);
  switch (module._type) {
    case 'hero':
      return <Hero hero={module} title={title} />;

    case 'copyWithImage':
      return <CopyWithImage copyWithImage={module} title={title} />;

    case 'servicesGrid':
      return <ServicesGrid servicesGrid={module} title={title} />;

    case 'headingWithCta':
      return <HeadingWithCta headingWithCta={module} title={title} />;

    case 'frequentlyAskedQuestions': {
      return title === 'Home' ? (
        <FAQsHome frequentlyAskedQuestions={module} />
      ) : (
        <FAQs frequentlyAskedQuestions={module} />
      );
    }

    case 'contactForm':
      return <ContactSection title={title} />;

    case 'googleMap':
      return <GoogleMap googleMap={module} />;

    case 'hoursOfOperation':
      return <HoursOfOperation hoursOfOperation={module} />;

    case 'reviews':
      return <Reviews reviews={module} title={title} />;

    case 'fees':
      return <Fees fees={module} />;

    case 'contactInformation':
      return <ContactSection title={title} />;

    case 'heading':
      return <Heading heading={module} />;

    case 'bookeoBookingWidget':
      return <BookeoBookingWidget bookeoBookingWidget={module} />;

    case 'bookeoVoucherWidget':
      return <BookeoVoucherWidget bookeoVoucherWidget={module} />;

    default:
      return null;
  }
}

export { SanityModules };
export type { TSanityModule };
